import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  IconButton,
  Typography,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Divider,
  Tooltip,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CodeIcon from "@mui/icons-material/Code";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

const OPERATORS = {
  text: [
    { value: "equals", label: "Equals" },
    { value: "not_equals", label: "Not Equals" },
    { value: "contains", label: "Contains" },
    // { value: "starts_with", label: "Starts With" },
    // { value: "ends_with", label: "Ends With" },
    { value: "in", label: "In (multiple values)" },
    { value: "not_in", label: "Not In (multiple values)" },
    // { value: "is_null", label: "Is Null" },
    // { value: "is_not_null", label: "Is Not Null" },
  ],
  number: [
    { value: "equals", label: "Equals" },
    { value: "not_equals", label: "Not Equals" },
    { value: "greater_than", label: "Greater Than" },
    { value: "less_than", label: "Less Than" },
    { value: "greater_than_or_equal", label: "Greater Than Or Equal" },
    { value: "less_than_or_equal", label: "Less Than Or Equal" },
    { value: "between", label: "Between" },
    { value: "in", label: "In (multiple values)" },
    // { value: "is_null", label: "Is Null" },
    // { value: "is_not_null", label: "Is Not Null" },
  ],
  date: [
    { value: "equals", label: "Equals" },
    { value: "not_equals", label: "Not Equals" },
    { value: "before", label: "Before" },
    { value: "after", label: "After" },
    { value: "between", label: "Between" },
    // { value: "is_null", label: "Is Null" },
    // { value: "is_not_null", label: "Is Not Null" },
  ],
};

const DEFAULT_CONDITION = {
  field: "",
  operator: "equals",
  value: "",
};

const DEFAULT_GROUP = {
  operator: "AND",
  conditions: [],
};

export default function FilterLogicBuilder({
  value,
  onChange,
  availableFields = [],
  fieldMetadata = {},
  onValidationChange,
}) {
  const [advancedMode, setAdvancedMode] = useState(false);
  const [advancedJson, setAdvancedJson] = useState("");
  const [validationError, setValidationError] = useState(null);

  // Normalize field options to a consistent object format
  const fieldOptions = useMemo(() => {
    if (!Array.isArray(availableFields)) return [];

    return availableFields
      .map((field) => {
        if (typeof field === "string") {
          return {
            name: field,
            description: fieldMetadata[field]?.description || "",
            type: fieldMetadata[field]?.type || "string",
          };
        }
        if (field && typeof field === "object" && field.name) {
          return {
            name: field.name,
            description: field.description || "",
            type: field.type || "string",
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [availableFields, fieldMetadata]);

  // Combine provided metadata with field option info
  const combinedFieldMetadata = useMemo(() => {
    const meta = { ...fieldMetadata };
    fieldOptions.forEach((field) => {
      meta[field.name] = {
        description: field.description || meta[field.name]?.description || null,
        type: field.type || meta[field.name]?.type || "string",
      };
    });
    return meta;
  }, [fieldMetadata, fieldOptions]);

  // Initialize structure from value
  useEffect(() => {
    if (value) {
      try {
        let parsed = typeof value === "string" ? JSON.parse(value) : value;
        setAdvancedJson(JSON.stringify(parsed, null, 2));
        
        // Validate structure
        if (!parsed.operator || !parsed.conditions) {
          // Convert old format to new format if needed
          parsed = convertToNewFormat(parsed);
          setAdvancedJson(JSON.stringify(parsed, null, 2));
        }
      } catch (e) {
        setAdvancedJson(JSON.stringify(DEFAULT_GROUP, null, 2));
      }
    } else {
      setAdvancedJson(JSON.stringify(DEFAULT_GROUP, null, 2));
    }
  }, [value]);

  // Convert old flat format to new nested format
  const convertToNewFormat = (oldValue) => {
    if (typeof oldValue !== "object" || oldValue === null) {
      return DEFAULT_GROUP;
    }

    // If it's already in new format
    if (oldValue.operator && Array.isArray(oldValue.conditions)) {
      return oldValue;
    }

    // Convert old flat format { field1: value1, field2: value2 } to new format
    const conditions = Object.entries(oldValue).map(([field, value]) => ({
      field,
      operator: Array.isArray(value) ? "between" : "equals",
      value,
    }));

    return {
      operator: "AND",
      conditions: conditions.length > 0 ? conditions : [],
    };
  };

  // Get operators for a field (uses metadata type if available, otherwise heuristic)
  const getFieldInputType = (fieldName) => {
    const fieldMeta = combinedFieldMetadata[fieldName];
    if (fieldMeta && fieldMeta.type) {
      const type = fieldMeta.type.toLowerCase();
      if (type === "date" || type === "datetime" || type === "timestamp") {
        return "date";
      }
      if (type === "number" || type === "integer" || type === "int" || type === "decimal" || type === "float" || type === "double") {
        return "number";
      }
    }
    return "text";
  };

  const getOperatorsForField = (fieldName) => {
    const fieldMeta = combinedFieldMetadata[fieldName];
    if (fieldMeta && fieldMeta.type) {
      const type = fieldMeta.type.toLowerCase();
      if (type === "date" || type === "datetime" || type === "timestamp") {
        return OPERATORS.date;
      }
      if (type === "number" || type === "integer" || type === "int" || type === "decimal" || type === "float" || type === "double") {
        return OPERATORS.number;
      }
      // Default to text for string, varchar, text, etc.
      return OPERATORS.text;
    }
    
    // Fallback to heuristic if no metadata
    const lowerField = fieldName.toLowerCase();
    if (lowerField.includes("date") || lowerField.includes("time")) {
      return OPERATORS.date;
    }
    if (
      lowerField.includes("id") ||
      lowerField.includes("count") ||
      lowerField.includes("amount") ||
      lowerField.includes("number")
    ) {
      return OPERATORS.number;
    }
    return OPERATORS.text;
  };

  // Validate structure (only validates complete conditions, allows incomplete ones during editing)
  const validateStructure = (structure, allowIncomplete = false) => {
    if (!structure || typeof structure !== "object") {
      return "Filter logic must be an object";
    }

    if (!structure.operator || !["AND", "OR"].includes(structure.operator)) {
      return "Top-level operator must be 'AND' or 'OR'";
    }

    if (!Array.isArray(structure.conditions)) {
      return "Conditions must be an array";
    }

    // Allow empty conditions - this means "wide open" (no filtering, will use 1=1)
    // Only validate if there are conditions present
    if (structure.conditions.length === 0) {
      return null; // Empty is valid - means no filtering
    }

    // Check for at least one complete condition
    let hasCompleteCondition = false;

    for (const condition of structure.conditions) {
      if (condition.operator && ["AND", "OR"].includes(condition.operator)) {
        // It's a nested group
        const nestedError = validateStructure(condition, allowIncomplete);
        if (nestedError) return nestedError;
        // If nested group has complete conditions, mark as having complete condition
        if (condition.conditions && condition.conditions.length > 0) {
          hasCompleteCondition = true;
        }
      } else {
        // It's a condition - check if it's complete
        const hasField = condition.field && condition.field.trim() !== "";
        const hasOperator = condition.operator && condition.operator.trim() !== "";
        const isMultiValueOp = ["in", "not_in"].includes(condition.operator);
        const hasValue = 
          ["is_null", "is_not_null"].includes(condition.operator) ||
          (isMultiValueOp 
            ? (condition.values !== undefined && Array.isArray(condition.values) && condition.values.length > 0)
            : (condition.value !== undefined && condition.value !== null && condition.value !== ""));

        // If condition is complete, mark it
        if (hasField && hasOperator && hasValue) {
          hasCompleteCondition = true;
        }

        // Only validate completeness if we're not allowing incomplete structures
        if (!allowIncomplete) {
          if (hasField && !hasOperator) {
            return `Condition for field '${condition.field}' must have an operator`;
          }
          if (hasField && hasOperator && !hasValue) {
            const valueLabel = isMultiValueOp ? "values" : "value";
            return `Condition for field '${condition.field}' must have a ${valueLabel}`;
          }
        }
      }
    }

    // Only require at least one complete condition when not allowing incomplete
    if (!allowIncomplete && !hasCompleteCondition && structure.conditions.length > 0) {
      return "At least one complete condition is required";
    }

    return null;
  };

  // Update structure and notify parent
  const updateStructure = (newStructure, allowIncomplete = true) => {
    // Allow incomplete structures during editing (when allowIncomplete is true)
    // Only validate strictly when saving (allowIncomplete is false)
    const error = validateStructure(newStructure, allowIncomplete);
    setValidationError(error);
    
    // Only notify parent of validation state if structure is complete
    // This allows users to build conditions without errors blocking them
    const isComplete = error === null;
    if (onValidationChange) {
      onValidationChange(isComplete);
    }

    // Always update the parent with the current structure, even if incomplete
    // This allows the form to save partial progress
    if (onChange) {
      onChange(newStructure);
    }
  };

  // Handle advanced mode JSON change
  const handleAdvancedJsonChange = (jsonString) => {
    setAdvancedJson(jsonString);
    try {
      const parsed = JSON.parse(jsonString);
      const error = validateStructure(parsed);
      setValidationError(error);
      if (onValidationChange) {
        onValidationChange(error === null);
      }
      if (error === null && onChange) {
        onChange(parsed);
      }
    } catch (e) {
      setValidationError("Invalid JSON format");
      if (onValidationChange) {
        onValidationChange(false);
      }
    }
  };

  // Add condition to a group
  const addCondition = (structure, path = []) => {
    const newCondition = { ...DEFAULT_CONDITION };
    const newStructure = JSON.parse(JSON.stringify(structure));

    if (path.length === 0) {
      newStructure.conditions.push(newCondition);
    } else {
      let target = newStructure;
      for (let i = 0; i < path.length; i++) {
        target = target.conditions[path[i]];
      }
      target.conditions.push(newCondition);
    }

    setValidationError(null);
    updateStructure(newStructure);
  };

  // Add group to a group
  const addGroup = (structure, path = []) => {
    const newGroup = { ...DEFAULT_GROUP };
    const newStructure = JSON.parse(JSON.stringify(structure));

    if (path.length === 0) {
      newStructure.conditions.push(newGroup);
    } else {
      let target = newStructure;
      for (let i = 0; i < path.length - 1; i++) {
        target = target.conditions[path[i]];
      }
      target.conditions.push(newGroup);
    }

    updateStructure(newStructure);
  };

  // Delete condition/group
  const deleteItem = (structure, path) => {
    const newStructure = JSON.parse(JSON.stringify(structure));

    if (path.length === 1) {
      newStructure.conditions.splice(path[0], 1);
    } else {
      let target = newStructure;
      for (let i = 0; i < path.length - 1; i++) {
        target = target.conditions[path[i]];
      }
      target.conditions.splice(path[path.length - 1], 1);
    }

    updateStructure(newStructure);
  };

  // Update condition
  const updateCondition = (structure, path, updates) => {
    const newStructure = JSON.parse(JSON.stringify(structure));
    let target = newStructure;

    for (let i = 0; i < path.length - 1; i++) {
      target = target.conditions[path[i]];
    }

    const condition = target.conditions[path[path.length - 1]];
    Object.assign(condition, updates);

    // Reset value/values if operator changed
    if (updates.operator) {
      if (["is_null", "is_not_null"].includes(updates.operator)) {
        condition.value = "";
        delete condition.values;
      } else if (["in", "not_in"].includes(updates.operator)) {
        // Switch to values array
        delete condition.value;
        if (!condition.values || !Array.isArray(condition.values)) {
          condition.values = [];
        }
      } else {
        // Switch to single value
        delete condition.values;
        if (condition.value === undefined) {
          condition.value = "";
        }
      }
    }

    updateStructure(newStructure);
  };

  // Update group operator
  const updateGroupOperator = (structure, path, operator) => {
    const newStructure = JSON.parse(JSON.stringify(structure));
    let target = newStructure;

    if (path.length === 0) {
      target.operator = operator;
    } else {
      for (let i = 0; i < path.length; i++) {
        target = target.conditions[path[i]];
      }
      target.operator = operator;
    }

    updateStructure(newStructure);
  };

  // Render a condition
  const renderCondition = (condition, index, path, structure) => {
    const isGroup = condition.operator && ["AND", "OR"].includes(condition.operator);
    const operators = condition.field
      ? getOperatorsForField(condition.field)
      : OPERATORS.text;
    const needsValue = !["is_null", "is_not_null"].includes(condition.operator);
    const isMultiValue = ["in", "not_in"].includes(condition.operator);
    const isBetween = condition.operator === "between";

    if (isGroup) {
      return (
        <Box key={index} sx={{ ml: path.length * 3, mb: 2 }}>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              border: "1px solid #e0e0e0",
              backgroundColor: "#f9f9f9",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <ToggleButtonGroup
                value={condition.operator}
                exclusive
                onChange={(e, newOp) => {
                  if (newOp) updateGroupOperator(structure, path, newOp);
                }}
                size="small"
              >
                <ToggleButton value="AND">AND</ToggleButton>
                <ToggleButton value="OR">OR</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" sx={{ ml: 2, color: "text.secondary" }}>
                Group
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <IconButton
                size="small"
                onClick={() => deleteItem(structure, path)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            {condition.conditions.map((cond, idx) =>
              renderCondition(cond, idx, [...path, idx], structure)
            )}

            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => addCondition(structure, path)}
              >
                Add Condition
              </Button>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => addGroup(structure, path)}
              >
                Add Group
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return (
      <Box key={index} sx={{ ml: path.length * 3, mb: 1.5 }}>
        <Paper elevation={0} sx={{ p: 1.5, border: "1px solid #e0e0e0" }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Field</InputLabel>
              <Select
                value={condition.field || ""}
                label="Field"
                onChange={(e) =>
                  updateCondition(structure, path, { field: e.target.value })
                }
              >
                {fieldOptions.map((field) => {
                  const metadata = combinedFieldMetadata[field.name];
                  return (
                    <MenuItem key={field.name} value={field.name}>
                      <Box>
                        <Typography variant="body2">{field.name}</Typography>
                        {metadata?.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {metadata.description}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Operator</InputLabel>
              <Select
                value={condition.operator || "equals"}
                label="Operator"
                onChange={(e) =>
                  updateCondition(structure, path, { operator: e.target.value })
                }
              >
                {operators.map((op) => (
                  <MenuItem key={op.value} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {needsValue && (
              <>
                {isMultiValue ? (
                  <TextField
                    size="small"
                    label="Values"
                    placeholder="Enter values separated by commas (e.g., value1, value2, value3)"
                    value={
                      condition._valuesInput !== undefined
                        ? condition._valuesInput
                        : Array.isArray(condition.values)
                          ? condition.values.join(", ")
                          : ""
                    }
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      // Store raw input string for free typing (allows commas)
                      updateCondition(structure, path, { 
                        _valuesInput: inputValue
                      });
                    }}
                    onBlur={(e) => {
                      // On blur, convert to array and clean up
                      const inputValue = e.target.value;
                      const valuesArray = inputValue
                        .split(",")
                        .map(v => v.trim())
                        .filter(v => v !== "");
                      updateCondition(structure, path, { 
                        values: valuesArray,
                        _valuesInput: valuesArray.join(", ") // Clean up display
                      });
                    }}
                    helperText="Separate multiple values with commas"
                    sx={{ minWidth: 300 }}
                  />
                ) : isBetween ? (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField
                      size="small"
                      label="From"
                      type={getFieldInputType(condition.field)}
                      value={
                        Array.isArray(condition.value) ? condition.value[0] || "" : ""
                      }
                      onChange={(e) => {
                        const currentValue = Array.isArray(condition.value)
                          ? condition.value
                          : [condition.value || "", ""];
                        currentValue[0] = e.target.value;
                        updateCondition(structure, path, { value: currentValue });
                      }}
                      sx={{ minWidth: 120 }}
                    />
                    <Typography>to</Typography>
                    <TextField
                      size="small"
                      label="To"
                      type={getFieldInputType(condition.field)}
                      value={
                        Array.isArray(condition.value) ? condition.value[1] || "" : ""
                      }
                      onChange={(e) => {
                        const currentValue = Array.isArray(condition.value)
                          ? condition.value
                          : [condition.value || "", ""];
                        currentValue[1] = e.target.value;
                        updateCondition(structure, path, { value: currentValue });
                      }}
                      sx={{ minWidth: 120 }}
                    />
                  </Box>
                ) : (
                  <TextField
                    size="small"
                    label="Value"
                    type={getFieldInputType(condition.field)}
                    value={condition.value || ""}
                    onChange={(e) =>
                      updateCondition(structure, path, { value: e.target.value })
                    }
                    sx={{ minWidth: 150 }}
                  />
                )}
            </>
            )}

            <IconButton
              size="small"
              onClick={() => deleteItem(structure, path)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      </Box>
    );
  };

  // Get current structure
  const getCurrentStructure = () => {
    try {
      if (advancedMode) {
        return JSON.parse(advancedJson);
      } else {
        const parsed = typeof value === "string" ? JSON.parse(value) : value;
        return parsed && parsed.operator ? parsed : convertToNewFormat(parsed || {});
      }
    } catch (e) {
      return DEFAULT_GROUP;
    }
  };

  // Convert filter logic structure to SQL WHERE clause
  const convertToSQL = (structure, indent = 0) => {
    if (!structure || !structure.conditions || structure.conditions.length === 0) {
      // Empty conditions means "wide open" - return 1=1 (always true, no filtering)
      return "1=1";
    }

    const indentStr = "  ".repeat(indent);
    const conditions = structure.conditions.map((condition, index) => {
      if (condition.operator && ["AND", "OR"].includes(condition.operator)) {
        // It's a nested group
        const nestedSQL = convertToSQL(condition, indent + 1);
        if (!nestedSQL) return null;
        return `${indentStr}(${nestedSQL}\n${indentStr})`;
      } else {
        // It's a condition
        const field = condition.field || "";
        const operator = condition.operator || "";
        
        if (!field || !operator) return null;

        let sqlCondition = "";

        switch (operator) {
          case "equals":
            sqlCondition = `${field} = '${condition.value || ""}'`;
            break;
          case "not_equals":
            sqlCondition = `${field} != '${condition.value || ""}'`;
            break;
          case "contains":
            sqlCondition = `${field} LIKE '%${condition.value || ""}%'`;
            break;
          case "starts_with":
            sqlCondition = `${field} LIKE '${condition.value || ""}%'`;
            break;
          case "ends_with":
            sqlCondition = `${field} LIKE '%${condition.value || ""}'`;
            break;
          case "in":
            if (Array.isArray(condition.values) && condition.values.length > 0) {
              const values = condition.values.map(v => `'${v}'`).join(", ");
              sqlCondition = `${field} IN (${values})`;
            } else {
              return null;
            }
            break;
          case "not_in":
            if (Array.isArray(condition.values) && condition.values.length > 0) {
              const values = condition.values.map(v => `'${v}'`).join(", ");
              sqlCondition = `${field} NOT IN (${values})`;
            } else {
              return null;
            }
            break;
          case "greater_than":
            sqlCondition = `${field} > ${condition.value || ""}`;
            break;
          case "less_than":
            sqlCondition = `${field} < ${condition.value || ""}`;
            break;
          case "greater_than_or_equal":
            sqlCondition = `${field} >= ${condition.value || ""}`;
            break;
          case "less_than_or_equal":
            sqlCondition = `${field} <= ${condition.value || ""}`;
            break;
          case "between":
            if (Array.isArray(condition.value) && condition.value.length === 2) {
              sqlCondition = `${field} BETWEEN ${condition.value[0] || ""} AND ${condition.value[1] || ""}`;
            } else {
              return null;
            }
            break;
          case "before":
            sqlCondition = `${field} < '${condition.value || ""}'`;
            break;
          case "after":
            sqlCondition = `${field} > '${condition.value || ""}'`;
            break;
          case "is_null":
            sqlCondition = `${field} IS NULL`;
            break;
          case "is_not_null":
            sqlCondition = `${field} IS NOT NULL`;
            break;
          default:
            return null;
        }

        return sqlCondition;
      }
    }).filter(Boolean);

    if (conditions.length === 0) return "";

    const operator = structure.operator || "AND";
    const separator = `\n${indentStr}${operator} `;
    
    return conditions.join(separator);
  };

  // Get SQL preview
  const getSQLPreview = () => {
    const currentStructure = getCurrentStructure();
    const whereClause = convertToSQL(currentStructure);
    
    if (!whereClause || whereClause === "1=1") {
      return "WHERE 1=1\n-- No filter conditions (wide open - returns all records)";
    }
    
    return `WHERE ${whereClause}`;
  };

  const currentStructure = getCurrentStructure();

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          FILTER LOGIC
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {/* <Tooltip title="Format JSON">
            <IconButton
              size="small"
              onClick={() => {
                try {
                  const parsed = JSON.parse(advancedJson);
                  const formatted = JSON.stringify(parsed, null, 2);
                  setAdvancedJson(formatted);
                  handleAdvancedJsonChange(formatted);
                } catch (e) {
                  // Invalid JSON
                }
              }}
            >
              <AutoFixHighIcon fontSize="small" />
            </IconButton>
          </Tooltip> */}
          <ToggleButtonGroup
            value={advancedMode ? "advanced" : "visual"}
            exclusive
            onChange={(e, newMode) => {
              if (newMode !== null) {
                setAdvancedMode(newMode === "advanced");
                if (newMode === "visual") {
                  // Sync advanced JSON to visual structure
                  try {
                    const parsed = JSON.parse(advancedJson);
                    updateStructure(parsed);
                  } catch (e) {
                    // Invalid JSON, keep current structure
                  }
                }
              }
            }}
            size="small"
          >
            <ToggleButton value="visual">
              <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />
              Visual
            </ToggleButton>
            <ToggleButton value="advanced">
              <CodeIcon fontSize="small" sx={{ mr: 0.5 }} />
              View JSON
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}

      {/* SQL Preview */}
      {!advancedMode && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
            SQL Preview:
          </Typography>
          <Box
            component="pre"
            sx={{
              backgroundColor: "#f5f5f5",
              p: 1.5,
              borderRadius: 1,
              overflowX: "auto",
              fontSize: "0.85rem",
              fontFamily: "monospace",
              border: "1px solid #e0e0e0",
              maxHeight: "200px",
              overflowY: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {getSQLPreview()}
          </Box>
        </Box>
      )}

      {advancedMode ? (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              This is a read-only view of the filter logic JSON structure. 
              Use the Visual mode to make changes.
            </Typography>
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={12}
            value={advancedJson}
            onChange={(e) => handleAdvancedJsonChange(e.target.value)}
            placeholder='{\n  "operator": "AND",\n  "conditions": [\n    {\n      "field": "field_name",\n      "operator": "equals",\n      "value": "value"\n    }\n  ]\n}'
            disabled
            sx={{
              "& .MuiInputBase-root": {
                fontFamily: "monospace",
                fontSize: "0.9rem",
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        </Box>
      ) : (
        <Box>
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={currentStructure.operator || "AND"}
              exclusive
              onChange={(e, newOp) => {
                if (newOp) updateGroupOperator(currentStructure, [], newOp);
              }}
              size="small"
            >
              <ToggleButton value="AND">AND</ToggleButton>
              <ToggleButton value="OR">OR</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" sx={{ ml: 2, color: "text.secondary" }}>
              (All conditions must match / Any condition can match)
            </Typography>
          </Box>

          {currentStructure.conditions.map((condition, index) =>
            renderCondition(condition, index, [index], currentStructure)
          )}

          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => addCondition(currentStructure, [])}
            >
              Add Condition
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => addGroup(currentStructure, [])}
            >
              Add Group
            </Button>
          </Box>

          {currentStructure.conditions.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No conditions yet. Click "Add Condition" to get started.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

